import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { graphql } from 'react-relay';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/types';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenFilledIcon from '@atlaskit/icon/core/eye-open-filled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import type { ApolloInteropMutableHelper } from '@confluence/apollo-relay-interop/entry-points/ApolloInteropMutableHelper.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import {
	useIsLicensed,
	useIsGuest,
} from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ManageWatcherDialogLoader } from '@confluence/manage-watcher-dialog/entry-points/ManageWatcherDialog.ts';
import { MANAGE_WATCHER_TAB_TYPES } from '@confluence/manage-watcher-dialog/entry-points/manage-watcher-tab-types.ts';
import { DialogsStateContainer } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { fg } from '@confluence/feature-gating';

import type { WatchSettingsItemTrialDetailsQuery as WatchSettingsItemTrialDetailsQuery$data } from './__types__/WatchSettingsItemTrialDetailsQuery.ts';
import { WatchSettingsCheckboxItems } from './WatchSettingsCheckboxItems.tsx';
import { UpdateContentType } from './UpdateType.ts';
import { CONFLUENCE_EDITION } from './watch-constants.js';
import { processWatchData } from './processWatchData.ts';
import { WatchDialogComponentWithWatchChildrenQuery } from './WatchDialogComponent.jsx';
import type {
	WatchDialogComponentWithWatchChildrenQuery$data,
	WatchDialogComponentWithWatchChildrenQuery$variables,
} from './__generated__/WatchDialogComponentWithWatchChildrenQuery.graphql.ts';

const i18n = defineMessages({
	watchSettings: {
		id: 'watch-dialog.overflow-menu.watch-settings',
		defaultMessage: 'Watch settings',
		description: 'Label for item in the overflow menu to open the watch settings menu',
	},
	manageWatchers: {
		id: 'watch-dialog.overflow-menu.manage-watchers',
		defaultMessage: 'Manage watchers',
		description: 'Label for a dropdown item option to manage the watchers on content',
	},
	watchingIconLabel: {
		id: 'watch-dialog.overflow-menu.watching-icon-label',
		defaultMessage: 'Watching content',
		description: 'Label for the eye icon indicating that the user is watching content',
	},
	notWatchingIconLabel: {
		id: 'watch-dialog.overflow-menu.not-watching-icon-label',
		defaultMessage: 'Not watching content',
		description: 'Label for the eye icon indicating that the user is not watching content',
	},
});

export enum WatchSettingsRadioOption {
	Unwatch = 'unwatch',
	ThisItem = 'this-item',
	ThisAndNewItems = 'this-and-new-items',
	AllContent = 'all-content',
}

type WatchSettingsItemComponentProps = {
	isMenuOpen: boolean;
	contentId: string;
	contentType: string;
	spaceId: string | undefined;
	spaceKey: string;
	setIsMenuOpen: (boolean: any) => void;
	flags: FlagsStateContainer;
	onMenuClose: () => void;
	onClick: () => void;
};

export const WatchSettingsItemComponent: React.FC<WatchSettingsItemComponentProps> = ({
	isMenuOpen,
	contentId,
	contentType,
	spaceId = '',
	spaceKey,
	setIsMenuOpen,
	flags,
	onMenuClose,
	onClick,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const updateQueryHandler = (watchTarget: UpdateContentType, newDataValue: any) => {
		const contentData = readQueryInterop<
			WatchDialogComponentWithWatchChildrenQuery$data,
			WatchDialogComponentWithWatchChildrenQuery$variables
		>({
			query: WatchDialogComponentWithWatchChildrenQuery,
			variables: { contentId },
		}) as ApolloInteropMutableHelper<WatchDialogComponentWithWatchChildrenQuery$data> | null;

		switch (watchTarget) {
			case UpdateContentType.CONTENT:
				if (contentData?.singleContent) {
					contentData.singleContent.currentUserIsWatching = newDataValue;
				}
				break;
			case UpdateContentType.CHILDREN:
				if (contentData?.singleContent) {
					contentData.singleContent.currentUserIsWatchingChildren = newDataValue;
				}
				break;
			case UpdateContentType.SPACE:
				if (contentData?.singleContent?.space) {
					contentData.singleContent.space.currentUser.isWatched = newDataValue;
				}
				break;
			default:
				if (contentData?.singleContent?.space) {
					contentData.singleContent.space.currentUser.isWatchingBlogs = newDataValue;
				}
				break;
		}

		if (contentData) {
			writeQueryInterop<
				WatchDialogComponentWithWatchChildrenQuery$data,
				WatchDialogComponentWithWatchChildrenQuery$variables
			>({
				query: WatchDialogComponentWithWatchChildrenQuery,
				data: contentData,
			});
		}
	};

	const { edition } = useSessionData();
	const isLicensed = useIsLicensed();
	const isGuest = useIsGuest();
	const isWatchEnabled = isLicensed || isGuest;

	const {
		data: watchDialogData,
		error: watchDialogError,
		loading: watchDialogDataLoading,
	} = useQueryInterop<
		WatchDialogComponentWithWatchChildrenQuery$data,
		WatchDialogComponentWithWatchChildrenQuery$variables
	>(WatchDialogComponentWithWatchChildrenQuery, {
		variables: { contentId },
		skip: !isWatchEnabled,
		onError: (error) => {
			if (isUnauthorizedError(error)) {
				markErrorAsHandled(error);
			}
		},
	});

	const {
		isCurrentUserWatching,
		isWatchingSpace,
		isWatchingBlogs,
		isWatchingChildren,
		hasAncestorWatchingChildren,
	} = processWatchData(watchDialogData);

	const isWatching = isWatchEnabled && (isCurrentUserWatching || isWatchingSpace);

	const { data: watchSettingsTrialDetailsData, error: watchSettingsTrialDetailsError } =
		useQueryInterop<WatchSettingsItemTrialDetailsQuery$data>(
			graphql`
				query WatchSettingsItemTrialDetailsQuery {
					license {
						trialEndDate
					}
				}
			`,
			{
				skip: !isWatchEnabled || edition === CONFLUENCE_EDITION.FREE,
				onError: (error) => {
					if (isUnauthorizedError(error)) {
						markErrorAsHandled(error);
					}
				},
			},
		);

	const handleWatchSettingsItemClick = useCallback(() => {
		onClick();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'content-watch-button',
				attributes: {
					isContentWrapper: fg('confluence_frontend_content_wrapper'),
				},
				source: 'overflowMenu',
			},
		}).fire();
	}, [createAnalyticsEvent, onClick]);

	const makeHandleManageWatchersClickHandler = (dialogs) => {
		return () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'ManageWatcherDialogButton',
					source: 'overflowMenu',
					attributes: {
						source: 'v2',
						isContentWrapper: fg('confluence_frontend_content_wrapper'),
					},
				},
			}).fire();
			dialogs.showDialog(ManageWatcherDialogLoader, {
				contentId,
				spaceId,
				spaceKey,
				contentType,
				defaultTab: MANAGE_WATCHER_TAB_TYPES.PAGE,
			});
			setIsMenuOpen(false);
		};
	};

	const isSpaceAdmin =
		watchDialogData?.singleContent?.space?.operations?.some(
			(op) => op?.operation === 'administer',
		) ?? false;
	let inTrial = false;
	if (watchSettingsTrialDetailsError) {
		inTrial = true;
	} else {
		const trialEndDate = watchSettingsTrialDetailsData?.license?.trialEndDate;
		if (trialEndDate) {
			const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(trialEndDate) ?? null;

			inTrial = trialDaysLeft !== null && trialDaysLeft > 0;
		}
	}

	return (
		<DropdownMenu
			placement="left-start"
			isOpen={isMenuOpen}
			onOpenChange={({ isOpen }: OnOpenChangeArgs) => {
				if (!isOpen) {
					onMenuClose();
				}
			}}
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					isDisabled={!isWatchEnabled || watchDialogDataLoading}
					elemBefore={
						isWatching ? (
							<EyeOpenFilledIcon label={formatMessage(i18n.watchingIconLabel)} />
						) : (
							<EyeOpenIcon label={formatMessage(i18n.notWatchingIconLabel)} />
						)
					}
					elemAfter={<ChevronRightIcon label="" size="small" />}
					testId="nested-watch-menu-trigger"
					onClick={handleWatchSettingsItemClick}
				>
					<FormattedMessage {...i18n.watchSettings} />
				</DropdownItem>
			)}
		>
			<WatchSettingsCheckboxItems
				isCurrentUserWatching={isCurrentUserWatching}
				isWatchingSpace={isWatchingSpace}
				isWatchingBlogs={isWatchingBlogs}
				isWatchingChildren={isWatchingChildren}
				hasAncestorWatchingChildren={hasAncestorWatchingChildren}
				contentId={contentId}
				contentType={contentType}
				spaceId={spaceId}
				updateQueryHandler={updateQueryHandler}
				flags={flags}
			/>
			{isSpaceAdmin && edition !== CONFLUENCE_EDITION.FREE && !inTrial && !watchDialogError && (
				<DropdownItemGroup hasSeparator>
					<Subscribe to={[DialogsStateContainer]}>
						{(dialogs) => (
							<DropdownItem onClick={makeHandleManageWatchersClickHandler(dialogs)}>
								<FormattedMessage {...i18n.manageWatchers} />
							</DropdownItem>
						)}
					</Subscribe>
				</DropdownItemGroup>
			)}
		</DropdownMenu>
	);
};

export const WatchSettingsItem: React.ComponentType<
	Omit<WatchSettingsItemComponentProps, 'flags'>
> = withFlags(WatchSettingsItemComponent);
